import {
  Action,
  Combat,
  STANDARD_MOVES,
  attack,
  moveTable,
  type AttackSpec,
  type HitResponse,
  type MoveSet,
  type MoveTable,
} from "./combat.ts";
import { Movement as M, MOVEMENT_PACE } from "./movement.ts";

/** Normalized 0–100 (GDD §5.2): shown on the selection card and turned into numbers by `deriveKit`. */
export interface CharacterStats {
  speed: number;
  power: number;
  resistance: number;
  weight: number;
  range: number;
  slamCharge: number;
}

/** One move of a manifest: an attack without its slot id (the slot gives it). */
export type MoveSpec = Omit<AttackSpec, "id">;

export interface MoveSetSpec {
  light1: MoveSpec;
  light2: MoveSpec;
  light3: MoveSpec;
  /** S1 and S2: `null` leaves the button without an ability. */
  special1: MoveSpec | null;
  special2: MoveSpec | null;
  superSlam: MoveSpec;
  airAttack?: MoveSpec;
  dashAttack?: MoveSpec;
}

/**
 * Champion manifest (GDD §5.1, §10.3), schema version 2: identity, card,
 * stats and the whole moveset as data. Until the GLB models exist (asset
 * pipeline, GDD §5.3) `look` describes the placeholder body the client draws.
 */
export interface CharacterManifest {
  id: string;
  schemaVersion: 2;
  displayName: string;
  archetype: "brawler" | "mobile" | "zoner" | "tank" | "trickster";
  description: string;
  look: {
    build: "heavy" | "slim";
    /** Accent colour of the champion (#rrggbb), next to the player's own colour. */
    accent: string;
  };
  /** Model and icon, relative to the asset root; optional until the asset pipeline exists. */
  asset?: { model: string; icon: string };
  stats: CharacterStats;
  moveset: MoveSetSpec;
}

export const CHARACTER_SCHEMA_VERSION = 2;

/**
 * Everything the simulation and the server need about a champion, derived
 * once from its manifest. Plain numbers computed with + - * / only, so the
 * client and the server derive bit-identical values (docs/DECISIONS.md, D3).
 */
export interface FighterKit {
  readonly form?:'beetle'|'aura'|'wig'|'horse';
  /** A player who has not picked a champion yet (D49): walks and floats, no attack, nobody can touch it. */
  readonly ghost?: true;
  readonly id: string;
  readonly moves: MoveSet;
  /** `moves` indexed by `Action`. */
  readonly table: MoveTable;
  readonly maxSpeed: number;
  readonly dashSpeed: number;
  readonly jumpSpeed: number;
  readonly response: HitResponse;
  /** Multiplier on the Slam meter earned by this champion's hits. */
  readonly slamChargeRate: number;
}

/** Linear map of a 0–100 stat onto [at0, at100]. */
function byStat(value: number, at0: number, at100: number): number {
  return at0 + ((at100 - at0) * value) / 100;
}

/**
 * Stats → numbers (D21). A stat of 50 gives the neutral movement values.
 * Power and range describe the moveset on
 * the card; the attacks carry their own numbers.
 */
export function deriveKit(id: string, stats: CharacterStats, moves: MoveSet): FighterKit {
  return Object.freeze({
    id,
    moves,
    table: moveTable(moves),
    maxSpeed: byStat(stats.speed, 5.5, 8.5) * MOVEMENT_PACE.run,
    dashSpeed: byStat(stats.speed, 15, 19) * MOVEMENT_PACE.dash,
    jumpSpeed: byStat(stats.weight, 12, 11),
    response: Object.freeze({
      knockbackTaken: byStat(stats.weight, 1.2, 0.8),
      hitstunTaken: byStat(stats.resistance, 1.15, 0.85),
    }),
    slamChargeRate: byStat(stats.slamCharge, 0.8, 1.2),
  });
}

const NEUTRAL_STATS: CharacterStats = { speed: 50, power: 50, resistance: 50, weight: 50, range: 50, slamCharge: 50 };

/** The Phase 1 fighter: standard moves, neutral stats, no abilities. Used by tests and as a fallback. */
export const STANDARD_KIT: FighterKit = deriveKit("standard", NEUTRAL_STATS, STANDARD_MOVES);

/** Builds the kit of a (validated) manifest. */
export function buildKit(def: CharacterManifest): FighterKit {
  const m = def.moveset;
  const moves: MoveSet = Object.freeze({
    light1: attack({ id: Action.Light1, ...m.light1 }),
    light2: attack({ id: Action.Light2, ...m.light2 }),
    light3: attack({ id: Action.Light3, ...m.light3 }),
    special1: m.special1 ? attack({ id: Action.Special1, ...m.special1 }) : null,
    special2: m.special2 ? attack({ id: Action.Special2, ...m.special2 }) : null,
    superSlam: attack({ id: Action.SuperSlam, ...m.superSlam }),
    ...(m.airAttack ? { airAttack: attack({ id: Action.AirAttack, ...m.airAttack }) } : {}),
    ...(m.dashAttack ? { dashAttack: attack({ id: Action.DashAttack, ...m.dashAttack }) } : {}),
  });
  return deriveKit(def.id, def.stats, moves);
}

const SLOTS = ["light1", "light2", "light3", "special1", "special2", "superSlam", "airAttack", "dashAttack"] as const;
const SLOT_ACTION: Record<(typeof SLOTS)[number], number> = {
  light1: Action.Light1,
  light2: Action.Light2,
  light3: Action.Light3,
  special1: Action.Special1,
  special2: Action.Special2,
  superSlam: Action.SuperSlam,
  airAttack: Action.AirAttack,
  dashAttack: Action.DashAttack,
};
/** Moves a chain (`nextLight`) may lead to: the normal attacks, never abilities or the Super Slam. */
const CHAINABLE: readonly number[] = [Action.None, Action.Light1, Action.Light2, Action.Light3];

/** Returns a list of problems; empty means the manifest is usable. */
export function validateCharacter(def: CharacterManifest): string[] {
  const errors: string[] = [];
  if (def.schemaVersion !== CHARACTER_SCHEMA_VERSION) errors.push(`schemaVersion ${String(def.schemaVersion)} non supportata`);
  if (!/^[a-z0-9_]{1,64}$/.test(def.id)) errors.push(`id "${def.id}" non valido (a-z, 0-9, _)`);
  if (!def.displayName) errors.push("displayName mancante");
  if (!/^#[0-9a-f]{6}$/i.test(def.look?.accent ?? "")) errors.push("look.accent deve essere #rrggbb");
  for (const [key, value] of Object.entries(def.stats)) {
    if (!(value >= 0 && value <= 100)) errors.push(`stats.${key} fuori da 0–100`);
  }

  for (const slot of SLOTS) {
    const move = def.moveset[slot];
    if (!move && (slot === "airAttack" || slot === "dashAttack")) continue;
    if (move === null && (slot === "special1" || slot === "special2")) continue;
    if (!move) {
      errors.push(`moveset.${slot} mancante`);
      continue;
    }
    errors.push(...validateMove(slot, move).map((e) => `moveset.${slot}: ${e}`));
  }

  const superSlam = def.moveset.superSlam;
  if (superSlam) {
    if (superSlam.startup !== Combat.superStartup) errors.push(`moveset.superSlam: startup deve essere ${Combat.superStartup}`);
    if ((superSlam.armorTicks ?? 0) !== superSlam.startup) errors.push("moveset.superSlam: armorTicks deve coprire tutta la carica");
    if (superSlam.projectile) errors.push("moveset.superSlam: non può essere un proiettile");
  }
  for (const slot of ["special1", "special2"] as const) {
    const move = def.moveset[slot];
    if (move && !((move.cooldown ?? 0) > 0)) errors.push(`moveset.${slot}: un'abilità deve avere un cooldown (GDD §5.4)`);
  }
  return errors;
}

function validateMove(slot: (typeof SLOTS)[number], m: MoveSpec): string[] {
  const errors: string[] = [];
  if (!m.name) errors.push("name mancante");
  const ints: [string, number, number, number][] = [
    ["startup", m.startup, 1, 60],
    ["active", m.active, 1, 60],
    ["recovery", m.recovery, 0, 120],
    ["hitstun", m.hitstun, 0, 120],
    ["cancelFrom", m.cancelFrom, 0, 255],
    ["cooldown", m.cooldown ?? 0, 0, 65535],
    ["armorTicks", m.armorTicks ?? 0, 0, 255],
    ["counterFrom", m.counterFrom ?? 0, 0, 255],
    ["counterUntil", m.counterUntil ?? 0, 0, 255],
  ];
  for (const [key, value, min, max] of ints) {
    if (!Number.isInteger(value) || value < min || value > max) errors.push(`${key} deve essere un intero in ${min}–${max}`);
  }
  if (m.startup + m.active + m.recovery > 255) errors.push("durata totale oltre 255 passi");
  if ((m.counterUntil ?? 0)>0 && !((m.counterFrom ?? 0)<m.counterUntil! && m.counterUntil!<=m.startup)) errors.push("finestra di risposta non valida");
  const nonNegative: [string, number][] = [
    ["range", m.range],
    ["knockback", m.knockback],
    ["lift", m.lift],
    ["slamGain", m.slamGain],
    ["lunge", m.lunge],
    ["travel", m.travel ?? 0],
    ["airBounce", m.airBounce ?? 0],
    ["damage", m.damage ?? 0],
  ];
  for (const [key, value] of nonNegative) {
    if (!(value >= 0 && Number.isFinite(value))) errors.push(`${key} deve essere ≥ 0`);
  }
  if (!(m.arcDeg > 0 && m.arcDeg <= 360)) errors.push("arcDeg fuori da (0, 360]");
  if ((m.travel ?? 0) > M.dashSpeed * 1.5) errors.push("travel troppo alto (attraversa i muri sottili)");
  if (!CHAINABLE.includes(m.nextLight)) errors.push("nextLight deve essere un attacco normale");
  if (m.nextLight === SLOT_ACTION[slot]) errors.push("una mossa non può concatenarsi a se stessa");
  if (m.projectile) {
    const p = m.projectile;
    if (!(p.speed > 0 && p.speed <= 40)) errors.push("projectile.speed fuori da (0, 40]");
    if (!(Number.isInteger(p.lifetime) && p.lifetime > 0 && p.lifetime <= 120)) errors.push("projectile.lifetime fuori da 1–120");
    if (!(p.radius > 0 && p.radius <= 1)) errors.push("projectile.radius fuori da (0, 1]");
    if(p.height!==undefined && !(p.height>=0 && p.height<=M.height*2))errors.push("projectile.height fuori portata");
    if(p.aimAssistDeg!==undefined && !(p.aimAssistDeg>0 && p.aimAssistDeg<=45))errors.push("projectile.aimAssistDeg fuori da (0, 45]");
  }
  return errors;
}
